import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  styled,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Person as PersonIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  FileUpload as FileUploadIcon,
  Home as HomeIcon,
  Star as StarIcon,
  AccessTime as AccessTimeIcon,
  NavigateNext as NavigateNextIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  FilterList as FilterListIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Sort as SortIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { logout } from '../../store/slices/authSlice';
import { ThemeToggle } from '../theme/ThemeToggle';
import { createAndOpenNewDocument } from '../../utils/docsIntegration';
import { createAndOpenNewSpreadsheet } from '../../utils/gridIntegration';
import { useSnackbar } from 'notistack';

// Styled search component
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.default, 0.9),
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.default, 1),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
  transition: theme.transitions.create(['width', 'box-shadow']),
  '&:focus-within': {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.palette.text.primary,
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '30ch',
      '&:focus': {
        width: '40ch',
      },
    },
  },
}));

const StyledBreadcrumb = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.primary.main,
  },
  display: 'flex',
  alignItems: 'center',
}));



const NavigationButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  margin: theme.spacing(0, 0.5),
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
  },
}));

const TopBar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { isSidebarOpen } = useSelector((state: RootState) => state.ui);
  const { user } = useSelector((state: RootState) => state.auth);
  const currentFile = useSelector((state: RootState) => state.files.currentFile);
  const currentFolder = useSelector((state: RootState) => state.folders.currentFolder);
  const [creatingDoc, setCreatingDoc] = useState(false);
  const [creatingSheet, setCreatingSheet] = useState(false);

  // Menu state
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [quickActionsAnchorEl, setQuickActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [viewOptionsAnchorEl, setViewOptionsAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Menu open states
  const isUserMenuOpen = Boolean(userMenuAnchorEl);
  const isQuickActionsOpen = Boolean(quickActionsAnchorEl);
  const isViewOptionsOpen = Boolean(viewOptionsAnchorEl);

  // Breadcrumb generation
  const generateBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(x => x);

    // Preview pages: Home › My Files › filename (skip literal "Preview")
    if (paths[0] === 'preview') {
      const fileName = currentFile?.name || paths[1] || 'File';
      return [
        { path: '/', name: 'Home' },
        { path: '/files', name: 'My Files' },
        { path: location.pathname, name: fileName },
      ];
    }
    
    // Map static path segments to readable names
    const pathMap: Record<string, string> = {
      'files': 'Files',
      'favorites': 'Favorites',
      'shared-with-me': 'Shared with Me',
      'my-shared': 'My Shared',
      'trash': 'Trash',
      'settings': 'Settings',
      'profile': 'Profile',
      'dashboard': 'Dashboard',
      'recent': 'Recent Files',
      'search': 'Search',
      'activity': 'Activity',
    };
    
    if (paths.length === 0) {
      return [{ path: '/', name: 'Home' }];
    }
    
    return [{ path: '/', name: 'Home' }].concat(
      paths.map((segment, index) => {
        const fullPath = `/${paths.slice(0, index + 1).join('/')}`;
        const name = pathMap[segment] || segment;
        return { path: fullPath, name };
      })
    );
  };
  
  const breadcrumbs = generateBreadcrumbs();

  // Menu handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };



  const handleQuickActionsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setQuickActionsAnchorEl(event.currentTarget);
  };

  const handleQuickActionsClose = () => {
    setQuickActionsAnchorEl(null);
  };
  
  const handleViewOptionsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setViewOptionsAnchorEl(event.currentTarget);
  };

  const handleViewOptionsClose = () => {
    setViewOptionsAnchorEl(null);
  };


  const handleSidebarToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleLogout = () => {
    dispatch(logout() as any);
    navigate('/');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  // Navigation history handlers
  const handleBackNavigation = () => {
    navigate(-1);
  };

  const handleForwardNavigation = () => {
    navigate(1);
  };

  const handleUpNavigation = () => {
    const paths = location.pathname.split('/').filter(x => x);
    if (paths.length > 1) {
      navigate(`/${paths.slice(0, paths.length - 1).join('/')}`);
    } else {
      navigate('/');
    }
  };

  // Render navigation buttons
  const renderNavigationButtons = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
      <Tooltip title="Back">
        <NavigationButton size="small" onClick={handleBackNavigation}>
          <ArrowBackIcon fontSize="small" />
        </NavigationButton>
      </Tooltip>
      <Tooltip title="Forward">
        <NavigationButton size="small" onClick={handleForwardNavigation}>
          <ArrowForwardIcon fontSize="small" />
        </NavigationButton>
      </Tooltip>
      <Tooltip title="Up">
        <NavigationButton size="small" onClick={handleUpNavigation}>
          <ArrowUpwardIcon fontSize="small" />
        </NavigationButton>
      </Tooltip>
    </Box>
  );

  // Render breadcrumbs
  const renderBreadcrumbs = () => (
    <Breadcrumbs 
      separator={<ChevronRightIcon fontSize="small" color="action" />} 
      aria-label="breadcrumb"
      sx={{ 
        flexShrink: 1, 
        overflow: 'hidden',
        '& .MuiBreadcrumbs-li': {
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        
        return isLast ? (
          <Typography 
            key={crumb.path} 
            color="text.primary" 
            fontWeight={500}
            variant="body2"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {crumb.name}
          </Typography>
        ) : (
          <StyledBreadcrumb
            key={crumb.path}
            to={crumb.path}
            onClick={(e) => {
              e.preventDefault();
              navigate(crumb.path);
            }}
          >
            {index === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">{crumb.name}</Typography>
              </Box>
            ) : (
              <Typography variant="body2">{crumb.name}</Typography>
            )}
          </StyledBreadcrumb>
        );
      })}
    </Breadcrumbs>
  );

  // Render view options button and menu
  const renderViewOptions = () => (
    <>
      <Tooltip title="View options">
        <IconButton
          size="medium"
          edge="end"
          onClick={handleViewOptionsOpen}
          color="inherit"
        >
          <ViewModuleIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={viewOptionsAnchorEl}
        open={isViewOptionsOpen}
        onClose={handleViewOptionsClose}
        PaperProps={{
          elevation: 3,
          sx: { 
            minWidth: 200,
            zIndex: (theme) => theme.zIndex.modal,
          }
        }}
      >
        <MenuItem onClick={handleViewOptionsClose}>
          <ListItemIcon>
            <ViewListIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="List view" />
        </MenuItem>
        <MenuItem onClick={handleViewOptionsClose}>
          <ListItemIcon>
            <ViewModuleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Grid view" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleViewOptionsClose}>
          <ListItemIcon>
            <SortIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sort options" />
        </MenuItem>
        <MenuItem onClick={handleViewOptionsClose}>
          <ListItemIcon>
            <FilterListIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Filter files" />
        </MenuItem>
      </Menu>
    </>
  );

  // Render main component
  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.drawer + 2,
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        <IconButton
          color="inherit"
          aria-label="toggle sidebar"
          onClick={handleSidebarToggle}
          edge="start"
          sx={{ mr: 1 }}
        >
          <MenuIcon />
        </IconButton>

        {renderNavigationButtons()}
        
        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flexGrow: 1 }}>
          {renderBreadcrumbs()}
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search files and folders..."
              inputProps={{ 'aria-label': 'search' }}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearch}
            />
          </Search>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
     

          {/* Quick Actions Button */}
          <Tooltip title="Quick actions">
            <IconButton
              size="medium"
              edge="end"
              onClick={handleQuickActionsOpen}
              color="inherit"
              sx={{ ml: 1 }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>

          {/* Theme Toggle Button */}
          <ThemeToggle />

          {/* User Profile Button */}
          <Tooltip title="Account">
            <IconButton
              size="medium"
              onClick={handleUserMenuOpen}
              color="inherit"
              sx={{ ml: 1 }}
            >
              {user?.photoURL ? (
                <Avatar
                  src={user.photoURL}
                  alt={user.displayName || ''}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                  {user?.displayName?.charAt(0) || 'U'}
                </Avatar>
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchorEl}
          open={isUserMenuOpen}
          onClose={handleUserMenuClose}
          PaperProps={{
            elevation: 3,
            sx: { 
              minWidth: 200,
              zIndex: (theme) => theme.zIndex.modal,
            }
          }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
            {user?.photoURL ? (
              <Avatar
                src={user.photoURL}
                alt={user.displayName || ''}
                sx={{ width: 40, height: 40 }}
              />
            ) : (
              <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.main }}>
                {user?.displayName?.charAt(0) || 'U'}
              </Avatar>
            )}
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={500}>
                {user?.displayName || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email || ''}
              </Typography>
            </Box>
          </Box>
          
          <Divider />
          
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>
          
          <Divider />
          
          {/* Theme options */}
          <ThemeToggle placement="menuitem" />
          
          <Divider />
          
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>

        {/* Quick Actions Menu */}
        <Menu
          anchorEl={quickActionsAnchorEl}
          open={isQuickActionsOpen}
          onClose={handleQuickActionsClose}
          PaperProps={{
            elevation: 3,
            sx: { 
              minWidth: 200,
              zIndex: (theme) => theme.zIndex.modal,
            }
          }}
        >
          <MenuItem onClick={() => { handleQuickActionsClose(); navigate('/files?upload=blocked'); }}>
            <ListItemIcon>
              <FileUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Upload Files" />
          </MenuItem>

          <Divider />
          
          <MenuItem onClick={handleQuickActionsClose}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="New Folder" />
          </MenuItem>
          
          <MenuItem
            disabled={creatingDoc}
            onClick={async () => {
              handleQuickActionsClose();
              setCreatingDoc(true);
              try {
                await createAndOpenNewDocument(currentFolder?.id || null);
                enqueueSnackbar('Opening document in Docs…', { variant: 'success' });
              } catch (err: any) {
                enqueueSnackbar(err?.message || 'Failed to create document', { variant: 'error' });
              } finally {
                setCreatingDoc(false);
              }
            }}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={creatingDoc ? 'Creating…' : 'New Document'} />
          </MenuItem>
          <MenuItem
            disabled={creatingSheet}
            onClick={async () => {
              handleQuickActionsClose();
              setCreatingSheet(true);
              try {
                await createAndOpenNewSpreadsheet(currentFolder?.id || null);
                enqueueSnackbar('Opening spreadsheet in Grid…', { variant: 'success' });
              } catch (err: any) {
                enqueueSnackbar(err?.message || 'Failed to create spreadsheet', { variant: 'error' });
              } finally {
                setCreatingSheet(false);
              }
            }}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={creatingSheet ? 'Creating…' : 'New Spreadsheet'} />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar; 